import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, orderBy } from "firebase/firestore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Archive, ArchiveRestore, Copy, FileSpreadsheet, Plus, Trash2 } from "lucide-react";
import { db } from "../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../hooks/useFirestoreCollectionOnce";
import {
  useDeleteEvent,
  useDuplicateEvent,
  useUpdateEvent,
} from "../../hooks/useEventMutations";
import { useAuth } from "../../context/AuthContext";
import { useSnackbar } from "../../context/SnackbarContext";
import { PageHeader } from "../../components/PageHeader";
import { PhoneHeaderActions } from "../../components/phone/PhoneHeaderActions";
import type { OverflowAction } from "../../components/phone/OverflowMenu";
import { useIsPhone } from "../../hooks/useIsPhone";
import { DataTable, type Column } from "../../components/DataTable";
import { TypeToConfirmDialog } from "../../components/TypeToConfirmDialog";
import { formatDateRange } from "@event-planner/shared";
import type { Event } from "@event-planner/shared";
import { useEventVenue } from "../../hooks/useEventVenue";
import { ExcelEventImportDialog } from "./ExcelEventImportDialog";
import { DuplicateEventDialog } from "./DuplicateEventDialog";

/**
 * Per-row venue cell. Each row hits its own venue location lookup; for
 * an admin list of ~20 events that's fine. The per-row hook avoids
 * having to plumb a batched fetch up into the page.
 */
function VenueCell({ event }: { event: Event }) {
  const { venue } = useEventVenue(event);
  const text = [venue?.label, venue?.city].filter(Boolean).join(", ");
  return <>{text || "—"}</>;
}

export function EventListPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { remove, loading: deleting } = useDeleteEvent();
  const { update, loading: updating } = useUpdateEvent();
  const { duplicate, loading: duplicating } = useDuplicateEvent();
  const { user } = useAuth();
  const isPhone = useIsPhone();

  const [deleteTarget, setDeleteTarget] = useState<Event | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<Event | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const eventsQuery = useMemo(
    () => query(collection(db, "events"), orderBy("startDate", "asc")),
    [],
  );
  const {
    data: events,
    loading,
    error,
    refetch: refetchEvents,
  } = useFirestoreCollectionOnce<Event>(eventsQuery);

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id);
      showSuccess("Event deleted");
      setDeleteTarget(null);
      refetchEvents();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to delete event";
      showError(msg);
    }
  }

  async function handleDuplicate(name: string, newStartDate: string) {
    if (!duplicateTarget) return;
    try {
      const result = await duplicate(
        { sourceEventId: duplicateTarget.id, name, newStartDate },
        user?.email ?? undefined,
      );
      const { sessions, locations, groups } = result.counts;
      showSuccess(
        `Event duplicated — ${sessions} session${sessions === 1 ? "" : "s"}, ` +
          `${locations} location${locations === 1 ? "" : "s"}, ` +
          `${groups} group${groups === 1 ? "" : "s"} copied`,
      );
      setDuplicateTarget(null);
      navigate(`/events/${result.newEventId}`);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to duplicate event";
      showError(msg);
    }
  }

  async function handleArchiveToggle(event: Event) {
    const nextStatus = event.status === "archived" ? "draft" : "archived";
    try {
      await update(event.id, { status: nextStatus });
      showSuccess(
        nextStatus === "archived" ? "Event archived" : "Event unarchived",
      );
      refetchEvents();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to update event";
      showError(msg);
    }
  }

  const columns: Column<Event>[] = [
    {
      id: "name",
      label: "Name",
      render: (event) => event.name,
    },
    {
      id: "dates",
      label: "Dates",
      render: (event) => formatDateRange(event.startDate, event.endDate),
      phone: "secondary",
    },
    {
      id: "location",
      label: "Location",
      render: (event) => <VenueCell event={event} />,
      phone: "secondary",
    },
    {
      id: "status",
      label: "Status",
      phone: "trailing",
      render: (event) => (
        <Chip
          label={event.status}
          size="small"
          color={
            event.status === "published"
              ? "success"
              : event.status === "draft"
                ? "default"
                : "warning"
          }
        />
      ),
    },
    // Members count removed per ADR-015 — if restored, use a per-event
    // getCountFromServer query against eventMemberships rather than a
    // denormalized counter.
    {
      id: "actions",
      label: "",
      align: "right",
      phone: "hidden",
      render: (event) => {
        const isArchived = event.status === "archived";
        return (
          <Box sx={{ display: "inline-flex", gap: 0.5 }}>
            <Tooltip title={isArchived ? "Unarchive event" : "Archive event"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchiveToggle(event);
                }}
                disabled={updating}
                aria-label={
                  isArchived ? `Unarchive ${event.name}` : `Archive ${event.name}`
                }
              >
                {isArchived ? <ArchiveRestore size={18} /> : <Archive size={18} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate event">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDuplicateTarget(event);
                }}
                aria-label={`Duplicate ${event.name}`}
                data-testid="duplicate-event-button"
              >
                <Copy size={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete event">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(event);
                }}
                aria-label={`Delete ${event.name}`}
              >
                <Trash2 size={18} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // Phone: the archive, duplicate and delete icons move to each row's ⋮ menu.
  function eventRowActions(event: Event): OverflowAction[] {
    const isArchived = event.status === "archived";
    return [
      {
        label: isArchived ? "Unarchive" : "Archive",
        icon: isArchived ? <ArchiveRestore size={18} /> : <Archive size={18} />,
        onClick: () => handleArchiveToggle(event),
        disabled: updating,
      },
      {
        label: "Duplicate",
        icon: <Copy size={18} />,
        onClick: () => setDuplicateTarget(event),
        testId: "duplicate-event-button",
      },
      {
        label: "Delete",
        icon: <Trash2 size={18} />,
        onClick: () => setDeleteTarget(event),
        destructive: true,
      },
    ];
  }

  return (
    <Box data-testid="events-page">
      <PageHeader
        title="Events"
        action={
          isPhone ? (
            <PhoneHeaderActions
              primary={
                <Button
                  variant="contained"
                  startIcon={<Plus size={18} />}
                  onClick={() => navigate("/events/new")}
                  data-testid="new-event-button"
                >
                  Create Event
                </Button>
              }
              more={[
                {
                  label: "Import from Excel",
                  icon: <FileSpreadsheet size={18} />,
                  onClick: () => setImportOpen(true),
                  testId: "import-event-button",
                },
              ]}
            />
          ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FileSpreadsheet size={18} />}
              onClick={() => setImportOpen(true)}
              data-testid="import-event-button"
            >
              Import from Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => navigate("/events/new")}
              data-testid="new-event-button"
            >
              Create Event
            </Button>
          </Box>
          )
        }
      />
      {!loading && !error && events.length === 0 ? (
        <Typography color="text.secondary">
          No events yet. Create your first event to get started.
        </Typography>
      ) : (
        <DataTable
          columns={columns}
          data={events}
          loading={loading}
          error={error}
          ariaLabel="Events table"
          getRowKey={(event) => event.id}
          onRowClick={(event) => navigate(`/events/${event.id}`)}
          phoneRowActions={eventRowActions}
          phoneRowActionsLabel={(event) => `More actions for ${event.name}`}
        />
      )}

      <TypeToConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Event"
        message={
          deleteTarget
            ? `Permanently delete "${deleteTarget.name}"? This cascades through every session, speaker link, attendee assignment, RSVP, session note, check-in, survey, survey response, sponsor, notification, invitation, and membership for this event. This cannot be undone — archive the event instead if you want to hide it without losing data.`
            : ""
        }
        confirmText={deleteTarget?.name ?? ""}
        confirmLabel="Delete Event"
        loading={deleting}
      />

      <DuplicateEventDialog
        open={!!duplicateTarget}
        event={duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        onConfirm={handleDuplicate}
        loading={duplicating}
      />

      <ExcelEventImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => refetchEvents()}
      />
    </Box>
  );
}
